const IDENTITY_PROVIDER_DICT = new Map([
  ["access-people-hr", "Access People HR"],
  ["adp", "ADP"],
  ["apple", "Apple"],
  ["asana", "Asana"],
  ["auth0", "Auth0"],
  ["azure", "Azure"],
  ["aws", "AWS"],
  ["bamboohr", "BambooHR"],
  ["bamboo-hr", "BambooHR"],
  ["bitbucket", "Bitbucket"],
  ["box", "Box"],
  ["breathe-hr", "BreatheHR"],
  ["bubble", "Bubble"],
  ["cas", "CAS"],
  ["cezanne-hr", "Cezanne HR"],
  ["classlink", "Classlink"],
  ["clerk", "Clerk"],
  ["cloudflare", "Cloudflare"],
  ["cyberark", "CyberArk"],
  ["datadog", "Datadog"],
  ["dropbox", "Dropbox"],
  ["duo", "Duo"],
  ["firebase", "Firebase"],
  ["fourth", "Fourth"],
  ["generic-saml", "Generic SAML"],
  ["generic-oidc", "Generic OIDC"],
  ["github", "GitHub"],
  ["gitlab", "GitLab"],
  ["godaddy", "GoDaddy"],
  ["google", "Google"],
  ["google-analytics", "Google Analytics"],
  ["google-calendar", "Google Calendar"],
  ["google-drive", "Google Drive"],
  ["google-mail", "Google Mail"],
  ["google-cloud", "Google Cloud"],
  ["hibob", "HiBob"],
  ["hubspot", "Hubspot"],
  ["intercom", "Intercom"],
  ["intuit", "Intuit"],
  ["jumpcloud", "JumpCloud"],
  ["keycloak", "Keycloak"],
  ["lastpass", "LastPass"],
  ["linear", "Linear"],
  ["linkedin", "LinkedIn"],
  ["login-gov", "Login.gov"],
  ["microsoft", "Microsoft"],
  ["miniorange", "Miniorange"],
  ["net-iq", "NetIQ"],
  ["next-auth", "NextAuth"],
  ["notion", "Notion"],
  ["okta", "Okta"],
  ["onelogin", "OneLogin"],
  ["oracle", "Oracle"],
  ["personio", "Personio"],
  ["ping-identity", "Ping Identity"],
  ["react-native-expo", "React Native Expo"],
  ["rippling", "Rippling"],
  ["salesforce", "Salesforce"],
  ["segment", "Segment"],
  ["shibboleth", "Shibboleth"],
  ["simple-saml-php", "Simple SAML PHP"],
  ["slack", "Slack"],
  ["splunk", "Splunk"],
  ["stripe", "Stripe"],
  ["supabase", "Supabase"],
  ["vercel", "Vercel"],
  ["vmware", "VMware"],
  ["workday", "Workday"],
  ["workos", "WorkOS"],
  ["xero", "Xero"],
  ["zendesk", "Zendesk"],
  ["test-idp", "Test IDP"],
  ["discord", "Discord"],
  ["clever", "Clever"],
] as const);

type MapKey<T extends Map<unknown, unknown>> =
  T extends Map<infer K, unknown> ? K : never;

export type IdentityProvider = MapKey<typeof IDENTITY_PROVIDER_DICT>;

export function getIdentityProviderName(provider: IdentityProvider) {
  return IDENTITY_PROVIDER_DICT.get(provider);
}

/**
 * Maps DirectoryType values to their corresponding IdentityProvider icon.
 * Used by ProviderIcon to display the correct icon via the data-provider CSS attribute.
 */
const DIRECTORY_TYPE_TO_ICON: Record<string, IdentityProvider> = {
  "azure scim v2.0": "azure",
  bamboohr: "bamboohr",
  "breathe hr": "breathe-hr",
  "cezanne hr": "cezanne-hr",
  "cyberark scim v2.0": "cyberark",
  "fourth hr": "fourth",
  "generic scim v2.0": "workos",
  "gsuite directory": "google",
  hibob: "hibob",
  "jump cloud scim v2.0": "jumpcloud",
  "okta scim v2.0": "okta",
  "onelogin scim v2.0": "onelogin",
  "people hr": "access-people-hr",
  personio: "personio",
  "pingfederate scim v2.0": "ping-identity",
  "rippling scim v2.0": "rippling",
  s3: "aws",
  sftp: "workos",
  "sftp workday": "workday",
  workday: "workday",
  pending: "workos",
};

/**
 * Maps DirectoryType values to their display names.
 */
const DIRECTORY_TYPE_TO_NAME: Record<string, string> = {
  "azure scim v2.0": "Microsoft Entra ID",
  bamboohr: "BambooHR",
  "breathe hr": "Breathe HR",
  "cezanne hr": "Cezanne HR",
  "cyberark scim v2.0": "CyberArk",
  "fourth hr": "Fourth HR",
  "generic scim v2.0": "Generic SCIM",
  "gsuite directory": "Google Workspace",
  hibob: "HiBob",
  "jump cloud scim v2.0": "JumpCloud",
  "okta scim v2.0": "Okta",
  "onelogin scim v2.0": "OneLogin",
  "people hr": "People HR",
  personio: "Personio",
  "pingfederate scim v2.0": "PingFederate",
  "rippling scim v2.0": "Rippling",
  s3: "Amazon S3",
  sftp: "SFTP",
  "sftp workday": "Workday (SFTP)",
  workday: "Workday",
  pending: "Pending",
};

export function getDirectoryTypeIcon(
  directoryType: string,
): IdentityProvider | null {
  return DIRECTORY_TYPE_TO_ICON[directoryType] ?? null;
}

export function getDirectoryTypeName(directoryType: string): string {
  return DIRECTORY_TYPE_TO_NAME[directoryType] ?? directoryType;
}

export function isIdentityProvider(provider: unknown) {
  return (
    typeof provider === "string" &&
    IDENTITY_PROVIDER_DICT.has(provider as IdentityProvider)
  );
}
